// Formats a Date as YYYY-MM-DD using its local calendar date. Date.toISOString()
// converts to UTC first, which shifts the date across midnight for any reader
// whose local timezone isn't UTC+0 — this offsets by the local tz to compensate.
export function toLocalISODate(d: Date): string {
	const offset = d.getTimezoneOffset();
	const local = new Date(d.getTime() - offset * 60 * 1000);
	return local.toISOString().slice(0, 10);
}

export function todayISO(): string {
	return toLocalISODate(new Date());
}

// Monday of the week containing the given YYYY-MM-DD date, as YYYY-MM-DD.
export function mondayOf(dateISO: string): string {
	const d = new Date(`${dateISO}T00:00:00`);
	const daysSinceMonday = (d.getDay() + 6) % 7;
	d.setDate(d.getDate() - daysSinceMonday);
	return toLocalISODate(d);
}

// The 7 YYYY-MM-DD dates (Monday..Sunday) of the week starting on weekStartISO.
export function weekDates(weekStartISO: string): string[] {
	const start = new Date(`${weekStartISO}T00:00:00`);
	return Array.from({ length: 7 }, (_, i) => {
		const d = new Date(start);
		d.setDate(d.getDate() + i);
		return toLocalISODate(d);
	});
}

export function weekLabelFor(weekStartISO: string): string {
	const start = new Date(`${weekStartISO}T00:00:00`);
	const end = new Date(start);
	end.setDate(end.getDate() + 6);
	const fmtDate = (d: Date) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
	return `${fmtDate(start)} – ${fmtDate(end)}`;
}

// Integer day count since the Unix epoch, in UTC so it's immune to DST shifts
// (unlike local-time math, dividing a UTC midnight instant always lands on
// a whole number) — used as the chart's numeric x-axis position.
export function dayNumber(dateISO: string): number {
	const [y, m, d] = dateISO.split("-").map(Number);
	return Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
}

// Inverse of dayNumber for axis tick labels. Must format with timeZone: "UTC"
// since the value was encoded as a UTC midnight instant.
export function formatDayNumber(day: number): string {
	const d = new Date(day * 86_400_000);
	return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

export function formatDateLabel(dateISO: string): string {
	const d = new Date(`${dateISO}T00:00:00`);
	return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}
